import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Card,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  Stack,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import {
  MoreVertRounded as MoreVertIcon,
  VisibilityRounded as VisibilityIcon,
  SendRounded as SendIcon,
  FactCheckRounded as AssessmentIcon,
  PersonRounded as PersonIcon,
} from '@mui/icons-material';
import assessmentService from '../../services/assessmentService';
import { format, parseISO } from 'date-fns';
import PageHeader from '../../components/ui/PageHeader';
import SearchField from '../../components/ui/SearchField';
import StatusChip from '../../components/ui/StatusChip';
import EmptyState from '../../components/ui/EmptyState';
import LoadingState from '../../components/ui/LoadingState';

const AssessmentsList = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredAssessments, setFilteredAssessments] = useState([]);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAssessment, setSelectedAssessment] = useState(null);

  // Fetch assessments
  useEffect(() => {
    const fetchAssessments = async () => {
      try {
        setLoading(true);
        const data = await assessmentService.getAssessments();
        setAssessments(data);
      } catch (err) {
        console.error('Error fetching assessments:', err);
        setError('Failed to load assessments. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAssessments();
  }, []);

  // Filter assessments based on search term
  useEffect(() => {
    if (searchTerm) {
      const lowerCaseSearch = searchTerm.toLowerCase();
      const filtered = assessments.filter(
        (assessment) =>
          assessment.customer?.name?.toLowerCase().includes(lowerCaseSearch) ||
          assessment.customer?.email?.toLowerCase().includes(lowerCaseSearch) ||
          assessment.result?.category?.toLowerCase().includes(lowerCaseSearch) ||
          assessment.result?.recommendation?.toLowerCase().includes(lowerCaseSearch)
      );
      setFilteredAssessments(filtered);
    } else {
      setFilteredAssessments(assessments);
    }
    setPage(0);
  }, [searchTerm, assessments]);

  const handleMenuOpen = (event, assessment) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedAssessment(assessment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const [successMessage, setSuccessMessage] = useState(null);

  const handleSendToCrm = async (id) => {
    handleMenuClose();
    setError(null);
    setSuccessMessage(null);
    try {
      await assessmentService.sendToCrm(id);
      setSuccessMessage('Assessment sent to CRM.');
      setTimeout(() => setSuccessMessage(null), 4000);
    } catch (err) {
      console.error('Error sending assessment to CRM:', err);
      setError(
        err.response?.data?.error || 'Failed to send the assessment to CRM. Please try again.'
      );
    }
  };

  const scoreColor = (score) => (score > 100 ? 'error' : score > 50 ? 'warning' : 'success');

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return <LoadingState label="Loading assessments…" />;
  }

  const pagedAssessments = filteredAssessments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <PageHeader
        title="Roof Assessments"
        subtitle="Self-service roof assessments submitted by customers"
      />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        sx={{ mb: 2 }}
      >
        <SearchField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search assessments…"
          sx={{ maxWidth: { sm: 420 } }}
        />
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          {filteredAssessments.length} result{filteredAssessments.length === 1 ? '' : 's'}
        </Typography>
      </Stack>

      {filteredAssessments.length === 0 ? (
        <Card>
          <EmptyState
            icon={<AssessmentIcon />}
            title={searchTerm ? 'No matching assessments' : 'No assessments yet'}
            description={
              searchTerm
                ? 'Try a different search term.'
                : 'Roof assessments submitted by customers will appear here.'
            }
            color="success"
          />
        </Card>
      ) : isMobile ? (
        /* ------------------------- Mobile card list ------------------------- */
        <Stack spacing={1.5}>
          {pagedAssessments.map((assessment) => (
            <Card
              key={assessment._id}
              onClick={() => navigate(`/assessments/${assessment._id}`)}
              sx={{ p: 2, cursor: 'pointer', '&:active': { transform: 'scale(0.99)' } }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  {format(parseISO(assessment.createdAt), 'MMM dd, yyyy')}
                </Typography>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <StatusChip
                    status={assessment.result.urgency}
                    label={`Score ${assessment.result.score}`}
                    color={scoreColor(assessment.result.score)}
                  />
                  <IconButton
                    size="small"
                    aria-label="Assessment actions"
                    onClick={(e) => handleMenuOpen(e, assessment)}
                    sx={{ mr: -1 }}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 1 }}>
                <PersonIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
                <Typography variant="subtitle2" noWrap>
                  {assessment.customer?.name || 'Anonymous'}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }} noWrap>
                {assessment.result.category}
              </Typography>
              <Divider sx={{ my: 1.25 }} />
              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                <StatusChip status={assessment.result.urgency} label={`${assessment.result.urgency} urgency`} />
                <StatusChip
                  status="source"
                  color="info"
                  label={(assessment.source || '').replace('_', ' ') || 'unknown'}
                />
              </Stack>
            </Card>
          ))}
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAssessments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Per page:"
          />
        </Stack>
      ) : (
        /* --------------------------- Desktop table -------------------------- */
        <Card>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Customer</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Urgency</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pagedAssessments.map((assessment) => (
                  <TableRow
                    key={assessment._id}
                    hover
                    onClick={() => navigate(`/assessments/${assessment._id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{format(parseISO(assessment.createdAt), 'MMM dd, yyyy')}</TableCell>
                    <TableCell>
                      {assessment.customer ? (
                        <Stack direction="row" spacing={1} alignItems="center">
                          <Box
                            aria-hidden
                            sx={{
                              width: 30,
                              height: 30,
                              borderRadius: '10px',
                              display: 'grid',
                              placeItems: 'center',
                              bgcolor: alpha(theme.palette.primary.main, 0.12),
                              color: 'primary.main',
                            }}
                          >
                            <PersonIcon sx={{ fontSize: 16 }} />
                          </Box>
                          <Typography variant="body2" fontWeight={600}>
                            {assessment.customer.name}
                          </Typography>
                        </Stack>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Anonymous
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        status="score"
                        label={assessment.result.score}
                        color={scoreColor(assessment.result.score)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{assessment.result.category}</Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={assessment.result.urgency} />
                    </TableCell>
                    <TableCell>
                      <StatusChip
                        status="source"
                        color="info"
                        label={(assessment.source || '').replace('_', ' ') || 'unknown'}
                      />
                    </TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="Actions">
                        <IconButton size="small" onClick={(e) => handleMenuOpen(e, assessment)}>
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredAssessments.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            navigate(`/assessments/${selectedAssessment?._id}`);
          }}
        >
          <ListItemIcon>
            <VisibilityIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View details</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => handleSendToCrm(selectedAssessment?._id)}>
          <ListItemIcon>
            <SendIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Send to CRM</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AssessmentsList;
